/* ============================================================================
   Modulo: Shop Board — tabs + quick view

   Two mechanisms in one file:
     1. A segmented pill tab group (no measurement, so no resize listener).
     2. A quick-view dialog whose per-product payload is cloned from a
        <template> on open.

   This is modelled on `section-product-frame`'s quick view but deliberately
   does NOT share its code. Three things are done differently on purpose, each
   fixing something in the original:

     - The scroll lock is DERIVED from the DOM on every transition, never OWNED
       by open()/close(). An owned lock is the exact shape that left a
       merchant's whole store unscrollable in `section-header-pill` round three:
       anything that removes the dialog while it is open orphans the only code
       that can unlock, and `shopify:section:unload` does not fire on a live
       storefront.
     - The focus trap includes the dialog itself as a fallback stop, because
       `<shopify-accelerated-checkout>` puts its buttons in a shadow root that
       an offsetParent-filtered light-DOM walk cannot see, and Tab would
       otherwise leak out of the dialog past Buy it now.
     - Document listeners are wired exactly once, guarded on a window flag: an
       identical `<script src>` still executes once per tag, and the theme
       editor re-inserts them.
   ========================================================================== */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var ROOT_SEL = '[data-modulo-sbd]';
  var LOCK = 'modulo-sbd-qvlock';

  /* ── Logging ──────────────────────────────────────────────────────────────
     Reuses the app's existing storefront channel:
       POST /apps/modulo/storefront-error  ->  proxy.storefront-error route
         -> storefrontErrorReporter.server.ts -> logger.error() -> Sentry + Discord

     The server forwards a fixed field set, so anything we want to READ in
     Discord has to be in `msg` (which is also the dedupe key) or in `x.body`
     (which lands in `detail`, clamped and email-redacted). Extra keys are
     dropped, so there is no point inventing any.

     Privacy, matching the extension's helper exactly: origin + pathname only —
     never the query string, which routinely carries `?email=`, discount codes
     and search terms — and never the user agent. This payload reaches Sentry
     AND Discord.

     Two budgets, because they guard different things. The server's is 10/min
     PER SHOP across every storefront script, so a single broken Shop Board
     install could crowd out the bundle errors that budget exists for. The
     client cap below (3 distinct messages per page load, each reported once)
     is the courtesy that keeps this section from being that neighbour. */
  var LOG_ENDPOINT = '/apps/modulo/storefront-error';
  var LOG_CAP = 3;
  var logged = {};
  var logCount = 0;

  function debugOn() {
    try {
      return !!window.MODULO_DEBUG ||
        (window.localStorage && localStorage.getItem('modulo_debug') === '1');
    } catch (e) { return false; }
  }

  /* Never throws, never returns a value anything branches on: a reporting
     failure must not be able to take the section down with it. */
  function report(msg, detail) {
    try {
      var full = 'shop-board: ' + msg;
      if (logged[full]) return;
      logged[full] = true;
      if (logCount >= LOG_CAP) return;
      logCount++;

      var payload = JSON.stringify({
        msg: full,
        bundleId: '',
        url: location.origin + location.pathname,
        x: { body: detail ? String(detail).slice(0, 300) : undefined },
      });

      if (navigator.sendBeacon) navigator.sendBeacon(LOG_ENDPOINT, payload);
      else window.fetch(LOG_ENDPOINT, { method: 'POST', body: payload, keepalive: true })
        .catch(function () {});

      if (debugOn()) console.warn('[modulo-shop-board]', full, detail || '');
    } catch (e) { /* reporting is best-effort, always */ }
  }

  /* Wraps a handler so a throw is reported once and swallowed. An exception in
     a delegated document listener would otherwise kill every later listener on
     the page, which is a far worse failure than the one that caused it. */
  function guard(label, fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (e) {
        report(label + ' threw', (e && e.message) || String(e));
      }
    };
  }

  /* ── Scroll lock, derived ─────────────────────────────────────────────── */

  /* A dialog only counts as open if its section instance is still alive. A
     theme-editor re-render or a soft navigation can clone an already-open
     wrapper verbatim, which would otherwise be a drawer that LOOKS open with no
     listeners behind it — an undismissable dialog on an unscrollable page. */
  function liveOpenDialogs() {
    var all = document.querySelectorAll('[data-sbd-qvmodal]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hasAttribute('hidden')) out.push(all[i]);
    }
    return out;
  }

  function syncLock() {
    var open = liveOpenDialogs();
    if (open.length) document.documentElement.classList.add(LOCK);
    else document.documentElement.classList.remove(LOCK);
    return open.length;
  }

  /* ── Focus trap ───────────────────────────────────────────────────────── */

  var FOCUSABLE = 'a[href],button:not([disabled]),select:not([disabled]),' +
                  'input:not([disabled]):not([type="hidden"]),textarea:not([disabled]),' +
                  '[tabindex]:not([tabindex="-1"])';

  function focusables(dialog) {
    var list = dialog.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].offsetParent !== null || list[i] === document.activeElement) out.push(list[i]);
    }
    return out;
  }

  function trap(e, dialog) {
    if (e.key !== 'Tab') return;
    var items = focusables(dialog);
    /* No visible light-DOM stop (everything is inside a shadow root, or the
       dialog is mid-render): keep focus on the dialog rather than letting it
       escape to the page behind. */
    if (!items.length) {
      e.preventDefault();
      if (typeof dialog.focus === 'function') dialog.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialog)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── Quick view ───────────────────────────────────────────────────────── */

  var returnTo = null;

  function dialogForSection(sid) {
    return document.querySelector('[data-sbd-qvmodal][data-sbd-sid="' + sid + '"]');
  }

  function closeAll() {
    var open = liveOpenDialogs();
    for (var i = 0; i < open.length; i++) {
      open[i].setAttribute('hidden', '');
      var body = open[i].querySelector('[data-sbd-qvbody]');
      /* Emptied on close so the cloned <form> stops existing. A hidden
         duplicate product form is a second thing a theme's cart JS can find. */
      if (body) body.textContent = '';
    }
    syncLock();
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    returnTo = null;
  }

  function openQuickView(trigger) {
    var id = trigger.getAttribute('data-sbd-qv');
    /* No id is not an error: the trigger is a plain product link until the
       Liquid gives it a payload. Everything below IS an error, because the
       markup promised a dialog and could not produce one. */
    if (!id) return false;

    /* getElementById on the trigger's attribute VALUE, not a selector — the id
       carries section.id precisely so two sections on one page cannot collide
       into a wrong-product bug. */
    var tpl = document.getElementById(id);
    if (!tpl || !tpl.content) {
      report('quick view payload missing', 'id=' + id +
        (tpl ? ' (element found but carries no .content — not a <template>)' : ' (no element with this id)'));
      return false;
    }

    var section = trigger.closest ? trigger.closest(ROOT_SEL) : null;
    if (!section) {
      report('trigger outside its section', 'id=' + id);
      return false;
    }
    var sid = section.getAttribute('data-sbd-sid');
    var wrap = dialogForSection(sid);
    if (!wrap) {
      /* The dialog is relocated to <body> at init. Missing here means either
         init never ran for this section or something removed the node. */
      report('quick view dialog missing for section', 'sid=' + sid);
      return false;
    }

    var body = wrap.querySelector('[data-sbd-qvbody]');
    var dialog = wrap.querySelector('[data-sbd-qvdialog]');
    if (!body || !dialog) {
      report('quick view dialog is malformed',
        'sid=' + sid + ' body=' + !!body + ' dialog=' + !!dialog);
      return false;
    }

    /* Guard against a second open while one is already up: without this,
       returnTo is overwritten and the focus-return target is lost. */
    if (liveOpenDialogs().length) closeAll();

    body.textContent = '';
    body.appendChild(tpl.content.cloneNode(true));

    var title = body.querySelector('[data-sbd-qvtitle]');
    if (title) dialog.setAttribute('aria-label', title.textContent);

    wrap.removeAttribute('hidden');
    syncLock();
    returnTo = trigger;
    dialog.scrollTop = 0;
    if (typeof dialog.focus === 'function') dialog.focus();
    return true;
  }

  /* ── Tabs ─────────────────────────────────────────────────────────────── */

  function activate(section, index, moveFocus) {
    var tabs = section.querySelectorAll('[data-sbd-tab]');
    var panels = section.querySelectorAll('[data-sbd-panel]');
    var posters = section.querySelectorAll('[data-sbd-poster]');
    var i;

    for (i = 0; i < tabs.length; i++) {
      var on = String(tabs[i].getAttribute('data-sbd-tab')) === String(index);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].setAttribute('tabindex', on ? '0' : '-1');
      if (on && moveFocus && typeof tabs[i].focus === 'function') tabs[i].focus();
    }
    for (i = 0; i < panels.length; i++) {
      if (String(panels[i].getAttribute('data-sbd-panel')) === String(index)) {
        panels[i].removeAttribute('hidden');
      } else {
        panels[i].setAttribute('hidden', '');
      }
    }
    /* The poster swaps WITH the grid — that is the whole reason the poster is a
       per-tab block rather than a section-level setting. */
    for (i = 0; i < posters.length; i++) {
      if (String(posters[i].getAttribute('data-sbd-poster')) === String(index)) {
        posters[i].removeAttribute('hidden');
      } else {
        posters[i].setAttribute('hidden', '');
      }
    }
  }

  function initSection(section) {
    try {
      initSectionInner(section);
    } catch (e) {
      report('init threw', (e && e.message) || String(e));
      /* Leave the lock consistent even if init died halfway through relocating
         the dialog — a stuck lock is an unscrollable page. */
      try { syncLock(); } catch (e2) {}
    }
  }

  function initSectionInner(section) {
    if (section.getAttribute('data-sbd-ready') === 'true') return;
    section.setAttribute('data-sbd-ready', 'true');

    /* The dialog is relocated to <body> because `position: fixed` resolves
       against the viewport only while no ancestor is a containing block for
       fixed descendants — a theme's transform / filter / backdrop-filter /
       containment on `.shopify-section` or the section group wrapper would
       otherwise strand it mid-page (the `hpi` lesson). The section's inline
       custom properties are MERGED onto the wrapper, not assigned over it, so
       any style the wrapper already carries survives. */
    var wrap = section.querySelector('[data-sbd-qvmodal]');
    if (wrap) {
      var sid = wrap.getAttribute('data-sbd-sid');
      var stale = document.querySelectorAll('body > [data-sbd-qvmodal][data-sbd-sid="' + sid + '"]');
      for (var s = 0; s < stale.length; s++) {
        if (stale[s] !== wrap && stale[s].parentNode) stale[s].parentNode.removeChild(stale[s]);
      }
      var tokens = section.getAttribute('style');
      if (tokens) {
        var own = wrap.getAttribute('style') || '';
        wrap.setAttribute('style', own ? own.replace(/;?\s*$/, ';') + tokens : tokens);
      }
      /* A wrapper cloned in an already-open state has no live instance behind
         it, so it is CLOSED rather than honoured. */
      wrap.setAttribute('hidden', '');
      document.body.appendChild(wrap);
    }

    var tabs = section.querySelectorAll('[data-sbd-tab]');
    if (tabs.length < 2) {
      syncLock();
      return;
    }

    section.addEventListener('keydown', function (e) {
      var tab = e.target.closest ? e.target.closest('[data-sbd-tab]') : null;
      if (!tab) return;
      var list = section.querySelectorAll('[data-sbd-tab]');
      var idx = -1;
      for (var i = 0; i < list.length; i++) if (list[i] === tab) idx = i;
      if (idx < 0) return;

      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % list.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + list.length) % list.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = list.length - 1;
      if (next === null) return;

      e.preventDefault();
      activate(section, list[next].getAttribute('data-sbd-tab'), true);
    });

    syncLock();
  }

  /* ── Document wiring (once) ───────────────────────────────────────────── */

  function wireDocument() {
    if (window.moduloSbdWired) return;
    window.moduloSbdWired = true;

    document.addEventListener('click', guard('click handler', function (e) {
      if (!e.target.closest) return;

      var tab = e.target.closest('[data-sbd-tab]');
      if (tab) {
        var section = tab.closest(ROOT_SEL);
        if (section) activate(section, tab.getAttribute('data-sbd-tab'), false);
        return;
      }

      if (e.target.closest('[data-sbd-qvclose]')) {
        e.preventDefault();
        closeAll();
        return;
      }

      /* A click inside the dialog is never a dismissal. */
      if (e.target.closest('[data-sbd-qvmodal]')) return;

      var trigger = e.target.closest('[data-sbd-qv]');
      if (trigger) {
        /* Only swallow the navigation if the dialog actually opened — falling
           through to the product page is the designed failure mode, and the
           reason a reported failure is still a usable page for the shopper. */
        if (openQuickView(trigger)) e.preventDefault();
      }
    }));

    document.addEventListener('keydown', guard('keydown handler', function (e) {
      var open = liveOpenDialogs();
      if (!open.length) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAll();
        return;
      }
      var dialog = open[0].querySelector('[data-sbd-qvdialog]');
      if (dialog) trap(e, dialog);
    }));

    /* JS never formats money: each <option> carries a Liquid-rendered price
       string, because a theme's own formatMoney is hostile (Ella throws on a
       one-argument call). */
    document.addEventListener('change', guard('variant change handler', function (e) {
      var sel = e.target && e.target.closest ? e.target.closest('[data-sbd-qvselect]') : null;
      if (!sel) return;
      var form = sel.closest('form');
      if (!form) return;
      var hidden = form.querySelector('[data-sbd-qvid]');
      if (hidden) hidden.value = sel.value;
      var opt = sel.options[sel.selectedIndex];
      var price = opt && opt.getAttribute('data-price');
      var node = form.parentNode ? form.parentNode.querySelector('[data-sbd-qvnow]') : null;
      if (node && price) node.textContent = price;
      else if (!price) report('variant option carries no price', 'value=' + sel.value);
    }));

    /* A bfcache restore can bring back a page whose lock class was set before
       the shopper navigated away. */
    window.addEventListener('pageshow', syncLock);
  }

  function bootstrap() {
    wireDocument();
    var els = document.querySelectorAll(ROOT_SEL);
    for (var i = 0; i < els.length; i++) initSection(els[i]);
    syncLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector(ROOT_SEL) : null;
    if (el) initSection(el);
    syncLock();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var sid = e.detail && e.detail.sectionId;
    if (sid) {
      var orphan = document.querySelector('body > [data-sbd-qvmodal][data-sbd-sid="' + sid + '"]');
      if (orphan && orphan.parentNode) orphan.parentNode.removeChild(orphan);
    }
    syncLock();
  });
})();
